// What the home page's Avisos (components/dashboard/EstaSemanaSection.js AvisosSection) shows,
// and for how long. Pure, so jest can test the time windows.
//
// Two kinds of aviso:
//  - council bulletins marked as avisos, for 48 h after the day they were published (only the
//    day is stored; it is taken as 08:00 Madrid, as before);
//  - works and closures from the public obras list (backend/services/obras.py, the same list the
//    map draws): an admin's corte total/parcial or a work marked urgent, and a DGT roadwork only
//    when the backend flags it (a full closure on a road Torrelavega uses, near town). They show
//    while they are on today or when they start tomorrow, and go when their end date and
//    schedule are over, not by publish date.
// A closure approved from a council post links to that post; the post itself is then not listed
// a second time.
import { madridDate, madridWallToMs, addDays } from './freshness';

// Not imported from ./obras: this module is in the main bundle (the home page), obras.js is not.
const esDgt = (z) => z?.origen === 'dgt';
const ddmm = (iso) => { const m = /^\d{4}-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? `${m[2]}/${m[1]}` : ''; };

export const BULLETIN_WINDOW_H = 48;
export const MAX_BULLETINS = 4;
export const MAX_OBRAS = 3;
const CORTES = new Set(['corte_total', 'corte_parcial']);

function ymd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  return m ? [+m[1], +m[2], +m[3]] : null;
}

/** Epoch ms of a Madrid wall-clock time on day 'YYYY-MM-DD' ('HH:MM', default midnight). */
export function madridMs(day, hhmm = '00:00') {
  const d = ymd(day);
  const t = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  if (!d || !t) return NaN;
  return madridWallToMs(d[0], d[1], d[2], +t[1], +t[2]);
}

/**
 * When a bulletin counts as published: 08:00 in Madrid on its day. This used to be
 * `fecha + 'T08:00:00+01:00'`, winter time all year: from April to October it was 09:00 Madrid,
 * so a morning aviso sat outside the window for an extra hour and left it an hour late.
 */
export const bulletinMs = (fecha) => madridMs(fecha, '08:00');

export function isAvisoBulletin(b) {
  // A WordPress category tag or the backend's aviso marker; never the title's wording.
  const hasAvisoCategory = b?.fuente === 'Ayuntamiento' && (b.categorias || []).some((c) => String(c).toLowerCase() === 'avisos');
  return hasAvisoCategory || b?.is_aviso === true;
}

const fold = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
// The same reading as the push (backend/services/bulletin_service.py is_urgent_aviso): a cut, or
// «emergencia» / «alerta» as a live notice. A bare 'emergencia' put «Ayudas de emergencia social»
// in the red «Aviso urgente» box, and a bare 'alerta' «el sistema de alertas por SMS».
// Since the push notices audit (27 Sep 2026), as there: «Aviso:» only with an urgent subject and
// from the Ayuntamiento; «sin agua» only with a local supply context; policy about cuts («por
// impago», protocols) is not a cut; interruptions, suspensions, evacuations and lockdowns are; a
// yellow alert is a regular aviso (bell only, no push), orange and red are urgent.
// One difference, on purpose: any «corte de …» is a cut here. The backend reads road closures with
// its own patterns and marks them corte_trafico; this list only holds bulletins already marked as
// avisos, and the broad reading covers ones stored before those patterns.
const CUT = /\bcortes? (?:de|del) |\bcorte puntual\b|\b(?:interrupcion|suspension|restriccion)(?:es)?\b(?:\s+\S+){0,4}?\s+(?:agua|suministro|luz|gas|fluido electrico)\b/;
const NOT_A_CUT = /\b(?:impagos?|protocolo|pobreza|vulnerables?|bono social|convenio|ordenanza|mocion|ayudas?|subvencion\w*)\b/;
const WITHOUT_SUPPLY = /\bsin (?:agua|luz|suministro)\b/;
const LOCAL_SUPPLY = /(?:\bc\/|\b(?:suministro|abastecimiento|red|redes|tuberia|averia|calles?|avenida|avda|plaza|paseo|barrios?|vecinos|viviendas|portales|torrelavega|sierrapando|tanos|ganzo|barreda|campuzano|dualez|viernoles|torres|covadonga|nueva ciudad|la inmobiliaria|zapaton|cuatro caminos|sorravides|mies de vega|la llama|linares|mortera|el cerezo|san ramon)\b)/;
const URGENT_ALERT = /\b(?:alerta|alarma) (?:roja|naranja|por)\b/;
const YELLOW = /\b(?:alerta|alarma|aviso|nivel) amarill[oa]s?\b/;
const ORANGE_RED = /\b(?:roj[oa]s?|naranjas?)\b/;
const EMERGENCY = [
  /^\W*emergencia\s*[:\-–—]/,
  /\b(?:aviso|alerta|situacion) de emergencia\b/,
  /\bemergencia (?:por|en) (?:(?:la|el|los|las) )?(?:calle|barrio|zona|rio|incendio|inundaci\w*|temporal|nevada|fuga|derrumbe|explosion)/,
];
const NOT_EMERGENCY = /\b(?:ayudas?|prestacion\w*|plan(?:es)?|declara\w*|declaracion\w*|tramitacion|tramita\w*|contrat\w*|obras?|expediente|subvencion\w*|fondos?|protocolo|simulacro|curso|jornadas?|formacion|licitacion)\b(?:\s+\S+){0,4}?\s+emergencia|\bemergencias? social(?:es)?\b/;
const EVACUATION = /\b(?:evacuacion|evacuad[oa]s?|evacuar|desalojo|desalojad[oa]s?|desalojar|confinamiento|confinad[oa]s?|nube toxica|fuga de gas|escape de gas)\b/;
const NOT_EVACUATION = /\b(?:simulacro|plan(?:es)?|protocolo|curso|jornadas?|formacion|ejercicio|charla|taller|licitacion|contrat\w*|obras?)\b(?:\s+\S+){0,3}?\s+(?:de\s+)?(?:evacuacion|desalojo|confinamiento)/;
const AVISO_PREFIX = /^\W*aviso(?: importante| a la (?:ciudadania|poblacion|vecindad))?\s*[:\-–—]/;
const AVISO_URGENT_SUBJECT = /\b(?:cortes?|cortad[oa]s?|cierres?|cerrad[oa]s?|emergencia|evacua\w*|desaloj\w*|confina\w*|toxic[oa]|fugas?|inundaci\w*|incendios?|derrumbes?|explosion|averias?|interrupcion|suspension|sin agua|sin luz|(?:alerta|alarma) (?:roja|naranja))\b/;
// Sources that publish live notices (backend _ALERT_SOURCES); no source: an older stored bulletin.
const liveSource = (fuente) => !fuente || fuente === 'Ayuntamiento';

function isSupplyCut(t) {
  if (NOT_A_CUT.test(t)) return false;
  return CUT.test(t) || (WITHOUT_SUPPLY.test(t) && LOCAL_SUPPLY.test(t));
}

/** Whether an aviso goes in the red «Aviso urgente» box rather than the Ayuntamiento's list. */
export function isUrgentAviso(b) {
  if (b?.aviso_type === 'corte_trafico') return true;
  if (b?.aviso_type === 'alerta_amarilla') return false;
  const t = fold(b?.titulo);
  if (isSupplyCut(t)) return true;
  if (!liveSource(b?.fuente)) return false;
  if (!NOT_EMERGENCY.test(t) && EMERGENCY.some((re) => re.test(t))) return true;
  if (EVACUATION.test(t) && !NOT_EVACUATION.test(t)) return true;
  if (/\baviso urgente\b/.test(t) || (AVISO_PREFIX.test(t) && AVISO_URGENT_SUBJECT.test(t))) return true;
  return URGENT_ALERT.test(t) && !(YELLOW.test(t) && !ORANGE_RED.test(t));
}

/** Bulletin avisos published in the last 48 hours. */
export function bulletinAvisos(bulletins, nowMs = Date.now()) {
  return (bulletins || []).filter((b) => {
    if (!b?.fecha) return false;
    const h = (nowMs - bulletinMs(b.fecha)) / 3.6e6;
    return h >= 0 && h <= BULLETIN_WINDOW_H && isAvisoBulletin(b);
  });
}

/** "20:00–07:00" / "de 20:00 a 7:00 h" / "20.00-07.00" → { ini: '20:00', fin: '07:00' }, else null. */
export function parseHorario(h) {
  const m = /(\d{1,2})[:.h](\d{2})\s*(?:h(?:oras)?\.?)?\s*(?:–|—|-|a|hasta(?: las)?)\s*(\d{1,2})[:.h](\d{2})/i.exec(String(h || ''));
  if (!m) return null;
  const pad = (x) => String(x).padStart(2, '0');
  const [hi, mi, hf, mf] = [+m[1], +m[2], +m[3], +m[4]];
  if (hi > 23 || hf > 24 || mi > 59 || mf > 59) return null;
  return { ini: `${pad(hi)}:${pad(mi)}`, fin: `${pad(hf % 24)}:${pad(mf)}` };
}

/**
 * [startMs, endMs] of a work in the obras list. An admin work runs from its start day (at the
 * schedule's start time, if any) to its end day (at the schedule's end time; an overnight
 * schedule on a one-day work ends the next morning), or with no end day until it is finished.
 * A DGT roadwork: its start and its authorised end.
 */
export function obraWindow(z) {
  if (esDgt(z)) {
    const s = Date.parse(z.inicio || '');
    const e = Date.parse(z.autorizada_hasta || '');
    return [Number.isNaN(s) ? -Infinity : s, Number.isNaN(e) ? Infinity : e];
  }
  const hor = parseHorario(z?.horario);
  const start = madridMs(z?.fecha_inicio, hor ? hor.ini : '00:00');
  const fin = z?.fecha_fin || z?.fecha_fin_estimada;
  let end = Infinity;
  if (fin) {
    if (hor) {
      const overnight = hor.fin <= hor.ini;
      const endDay = overnight && fin <= z.fecha_inicio ? addDays(z.fecha_inicio, 1) : fin;
      end = madridMs(endDay, hor.fin);
    } else {
      end = madridMs(addDays(fin, 1), '00:00');     // the whole end day
    }
  }
  return [Number.isNaN(start) ? -Infinity : start, Number.isNaN(end) ? Infinity : end];
}

/** Whether a work of the public list belongs in the home Avisos at all (window aside). */
export function isObraAviso(z) {
  if (!z || z.estado === 'finalizada') return false;
  if (esDgt(z)) return z.aviso_portada === true;
  return CORTES.has(z.tipo) || z.urgente === true;
}

/**
 * The works to show now: on today (Madrid) or starting tomorrow, not yet over. Each with its
 * line, e.g. «Hoy 20:00–07:00 · Corte en la rotonda de Fernández Vallejo».
 */
export function obraAvisos(zones, nowMs = Date.now()) {
  const today = madridDate(nowMs);
  const tomorrow = addDays(today, 1);
  const horizon = madridMs(addDays(today, 2), '00:00');      // until the end of tomorrow
  const out = [];
  for (const z of zones || []) {
    if (!isObraAviso(z)) continue;
    const [start, end] = obraWindow(z);
    if (!(end > nowMs) || !(start < horizon)) continue;
    const hor = esDgt(z) ? null : parseHorario(z.horario);
    const startDay = Number.isFinite(start) ? madridDate(start) : '';
    const fin = z.fecha_fin || z.fecha_fin_estimada;
    let cuando;
    if (startDay === tomorrow) cuando = 'Mañana';
    else if (startDay === today) cuando = 'Hoy';
    else if (hor) cuando = 'Hoy';
    else if (fin && fin > today) cuando = `Hasta el ${ddmm(fin)}`;
    else if (fin) cuando = 'Hoy';
    else cuando = 'En curso';
    const texto = `${cuando}${hor ? ` ${hor.ini}–${hor.fin}` : ''} · ${z.nombre || 'Obras'}`;
    out.push({
      id: z.id, texto, start, end, zone: z, dgt: esDgt(z),
      detalle: esDgt(z) ? [z.km, z.sentido].filter(Boolean).join(' · ') : '',
      fuente_url: z.fuente_url || '',
    });
  }
  out.sort((a, b) => a.start - b.start);
  return out.slice(0, MAX_OBRAS);
}

/** 'https://torrelavega.es/noticia/x' for any spelling of the same link (as the backend's _url_key). */
export function urlKey(u) {
  return String(u || '').trim().toLowerCase().split('#')[0].replace(/\/+$/, '').replace(/^http:\/\//, 'https://');
}

/**
 * Everything the section shows: { obras, boletines }. A bulletin whose link is the source of a
 * work shown above is left out (the same closure, once).
 */
export function avisosPortada(bulletins, zones, nowMs = Date.now()) {
  const obras = obraAvisos(zones, nowMs);
  const shown = new Set(obras.map((o) => urlKey(o.fuente_url)).filter(Boolean));
  const boletines = bulletinAvisos(bulletins, nowMs).filter((b) => !shown.has(urlKey(b.enlace))).slice(0, MAX_BULLETINS);
  return { obras, boletines };
}
